import {createFileRoute} from '@tanstack/react-router';
import {makeApiFilterParam, UserStatus} from 'shared';
import {z} from 'zod';

import {CreateAssetForm} from '@/features/Asset/components/CreateAssetForm';
import {
  categoryKeys,
  categoryPresetKeys,
  layoutTemplateKeys,
  severityScaleKeys,
} from '@/features/Library/api/queryKeys';
import {getCategories} from '@/features/Library/api/readCategories';
import {getCategoryPresets} from '@/features/Library/api/readCategoryPresets';
import {getLayoutTemplates} from '@/features/Library/api/readLayoutTemplates';
import {getSeverityScales} from '@/features/Library/api/readSeverityScales';
import {siteKeys} from '@/features/Site/api/queryKeys';
import {getSites} from '@/features/Site/api/readSites';
import {userKeys} from '@/features/User/api/queryKeys';
import {getUsers} from '@/features/User/api/readUsers';
import {makeDocumentTitle} from '@/utils/makeDocumentTitle';

export const Route = createFileRoute('/_b/_p/assets/create')({
  component: CreateAssetForm,

  validateSearch: z.object({
    site: z.string().optional(),
  }),
  loaderDeps: ({search}) => ({...search}),

  beforeLoad: () => {
    makeDocumentTitle(`Create Asset`);

    const userFilter = {
      filter: makeApiFilterParam({
        prop: 'status',
        value: UserStatus.Activated,
        comparison: 'eq',
      }),
    };

    return {
      readSitesQuery: {
        queryKey: siteKeys.list(),
        queryFn: () => getSites(),
      },
      readCategoriesQuery: {
        queryKey: categoryKeys.list(),
        queryFn: () => getCategories(),
      },
      readCategoryPresetsQuery: {
        queryKey: categoryPresetKeys.list(),
        queryFn: () => getCategoryPresets(),
      },
      readSeveritiesQuery: {
        queryKey: severityScaleKeys.list(),
        queryFn: () => getSeverityScales(),
      },
      readUsersQuery: {
        queryKey: userKeys.list(userFilter),
        queryFn: () => getUsers(userFilter),
      },
      readLayoutTemplatesQuery: {
        queryKey: layoutTemplateKeys.list(),
        queryFn: () => getLayoutTemplates(),
      },
    };
  },

  loader({context}) {
    return Promise.all([
      context.queryClient.ensureQueryData(context.readSitesQuery),
      context.queryClient.ensureQueryData(context.readCategoriesQuery),
      context.queryClient.ensureQueryData(context.readCategoryPresetsQuery),
      context.queryClient.ensureQueryData(context.readSeveritiesQuery),
      context.queryClient.ensureQueryData(context.readUsersQuery),
      context.queryClient.ensureQueryData(context.readLayoutTemplatesQuery),
    ]);
  },
});
